import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  CircularProgress,
  Grid
} from '@mui/material';
import {
  Add as AddIcon,
  VisibilityOff as HideIcon
} from '@mui/icons-material';
import { useProfile } from '../../contexts/ProfileContext';
import { hasSystemPermission, SYSTEM_PERMISSIONS } from '../../utils/systemPermissions';
import {
  createSystemNotification,
  getAllSystemNotifications,
  deactivateSystemNotification,
  SYSTEM_NOTIFICATION_TYPES,
  SYSTEM_PRIORITY
} from '../../services/systemNotificationService';
import { formatDistanceToNow } from 'date-fns';
import logger from '../../utils/logger';

/**
 * SystemNotifications Component
 * Manage system-wide notifications
 */
const SystemNotifications = () => {
  const { profile } = useProfile();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    type: SYSTEM_NOTIFICATION_TYPES.ANNOUNCEMENT,
    title: '',
    message: '',
    priority: SYSTEM_PRIORITY.MEDIUM,
    targetAudience: 'all',
    actionUrl: '',
    actionLabel: '',
    expiresAt: null
  });

  const canCreate = hasSystemPermission(profile, SYSTEM_PERMISSIONS.CREATE_SYSTEM_NOTIFICATIONS);
  const canManage = hasSystemPermission(profile, SYSTEM_PERMISSIONS.MANAGE_SYSTEM_NOTIFICATIONS);

  // Load notifications
  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const data = await getAllSystemNotifications(true); // Include inactive
      setNotifications(data);
    } catch (err) {
      logger.error('Error loading notifications:', err);
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setDialogOpen(true);
    setError(null);
    setSuccess(null);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormData({
      type: SYSTEM_NOTIFICATION_TYPES.ANNOUNCEMENT,
      title: '',
      message: '',
      priority: SYSTEM_PRIORITY.MEDIUM,
      targetAudience: 'all',
      actionUrl: '',
      actionLabel: '',
      expiresAt: null
    });
  };

  const handleSubmit = async () => {
    try {
      setError(null);

      if (!formData.title || !formData.message) {
        setError('Title and message are required');
        return;
      }

      await createSystemNotification({
        ...formData,
        createdBy: profile.uid,
        createdByName: profile.displayName || profile.email
      });

      setSuccess('System notification created successfully!');
      handleCloseDialog();
      loadNotifications();
    } catch (err) {
      logger.error('Error creating notification:', err);
      setError('Failed to create notification: ' + err.message);
    }
  };

  const handleDeactivate = async (notificationId) => {
    if (!window.confirm('Are you sure you want to deactivate this notification?')) {
      return;
    }

    try {
      await deactivateSystemNotification(notificationId);
      setSuccess('Notification deactivated');
      loadNotifications();
    } catch (err) {
      logger.error('Error deactivating notification:', err);
      setError('Failed to deactivate notification');
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case SYSTEM_NOTIFICATION_TYPES.UPDATE:
        return 'primary';
      case SYSTEM_NOTIFICATION_TYPES.MAINTENANCE:
        return 'warning';
      case SYSTEM_NOTIFICATION_TYPES.TIP:
        return 'info';
      case SYSTEM_NOTIFICATION_TYPES.ALERT:
        return 'error';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case SYSTEM_PRIORITY.URGENT:
        return 'error';
      case SYSTEM_PRIORITY.HIGH:
        return 'warning';
      case SYSTEM_PRIORITY.MEDIUM:
        return 'info';
      default:
        return 'default';
    }
  };

  if (!canCreate && !canManage) {
    return (
      <Box>
        <Alert severity="error">
          You do not have permission to access this page.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          System Notifications
        </Typography>
        {canCreate && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
          >
            Create Notification
          </Button>
        )}
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Notifications Table */}
      <TableContainer component={Paper} sx={{ bgcolor: 'grey.800' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Message</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Audience</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : notifications.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No notifications yet. Create your first system notification!
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              notifications.map((notification) => (
                <TableRow key={notification.id}>
                  <TableCell>
                    <Chip
                      label={notification.type}
                      size="small"
                      color={getTypeColor(notification.type)}
                    />
                  </TableCell>
                  <TableCell>{notification.title}</TableCell>
                  <TableCell>
                    {notification.message.substring(0, 50)}
                    {notification.message.length > 50 && '...'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={notification.priority}
                      size="small"
                      color={getPriorityColor(notification.priority)}
                    />
                  </TableCell>
                  <TableCell>{notification.targetAudience}</TableCell>
                  <TableCell>
                    {notification.createdAt?.toDate
                      ? formatDistanceToNow(notification.createdAt.toDate(), { addSuffix: true })
                      : 'Unknown'}
                  </TableCell>
                  <TableCell>
                    {notification.isActive ? (
                      <Chip label="Active" color="success" size="small" />
                    ) : (
                      <Chip label="Inactive" color="default" size="small" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {canManage && notification.isActive && (
                      <IconButton
                        size="small"
                        onClick={() => handleDeactivate(notification.id)}
                        title="Deactivate"
                      >
                        <HideIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { bgcolor: 'grey.800' }
        }}
      >
        <DialogTitle>Create System Notification</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={formData.type}
                  label="Type"
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                >
                  {Object.values(SYSTEM_NOTIFICATION_TYPES).map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formData.priority}
                  label="Priority"
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                >
                  {Object.values(SYSTEM_PRIORITY).map((priority) => (
                    <MenuItem key={priority} value={priority}>
                      {priority}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                multiline
                rows={4}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Target Audience</InputLabel>
                <Select
                  value={formData.targetAudience}
                  label="Target Audience"
                  onChange={(e) => setFormData({ ...formData, targetAudience: e.target.value })}
                >
                  <MenuItem value="all">All Users</MenuItem>
                  <MenuItem value="specific">Specific Users</MenuItem>
                  <MenuItem value="family_code">Specific Families</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Action URL (optional)"
                value={formData.actionUrl}
                onChange={(e) => setFormData({ ...formData, actionUrl: e.target.value })}
                placeholder="/page-url"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Action Label (optional)"
                value={formData.actionLabel}
                onChange={(e) => setFormData({ ...formData, actionLabel: e.target.value })}
                placeholder="Learn More"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            Create Notification
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SystemNotifications;
